import { useCallback, useRef, useState } from "react";
function getSpeechRecognitionCtor() {
    const w = window;
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
export function useSpeechRecognition(onResult) {
    const [listening, setListening] = useState(false);
    const [error, setError] = useState(null);
    const recognitionRef = useRef(null);
    const supported = typeof window !== "undefined" && getSpeechRecognitionCtor() !== null;
    const stop = useCallback(() => {
        recognitionRef.current?.stop();
    }, []);
    const start = useCallback(() => {
        const Ctor = getSpeechRecognitionCtor();
        if (!Ctor)
            return;
        setError(null);
        // iOS Safari specifically, confirmed live: starting a fresh SpeechRecognition instance right
        // after a previous one's session is a known source of "says it's listening, never actually
        // picks up audio" -- the native speech/audio session from the prior instance (and from any TTS
        // playback that happened in between) doesn't appear to always be fully torn down by the time a
        // new `.start()` is called, even though the JS-level call succeeds with no error. `.abort()`
        // (not just relying on `.stop()`/`onend` having already fired) forces an immediate, explicit
        // teardown of anything still lingering on the previous instance before this one starts.
        if (recognitionRef.current) {
            recognitionRef.current.onend = null;
            recognitionRef.current.onresult = null;
            recognitionRef.current.onerror = null;
            try {
                recognitionRef.current.abort();
            }
            catch {
                // Nothing to clean up if it was already stopped.
            }
            recognitionRef.current = null;
        }
        const recognition = new Ctor();
        recognition.lang = "en-US";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.onresult = (event) => {
            const transcript = event.results[0]?.[0]?.transcript;
            if (transcript)
                onResult(transcript);
        };
        recognition.onerror = (event) => {
            // "no-speech" (silence/no-speech timeout) and "aborted" (our own deliberate `.abort()` above,
            // or a caller-initiated `stop()`) are routine and expected, especially in continuous
            // "conversation mode" (VoiceOrbExperience.tsx) where listening restarts after every reply --
            // surfacing them as a user-facing error would make a normal pause between sentences look like
            // a malfunction. Only genuine problems (permission denied, no mic, network) get shown.
            if (event.error === "no-speech" || event.error === "aborted") {
                setListening(false);
                return;
            }
            setError(event.error === "not-allowed" ? "Microphone permission denied." : "Couldn't hear that clearly -- try again.");
            setListening(false);
        };
        recognition.onend = () => setListening(false);
        recognitionRef.current = recognition;
        // `.start()` can throw SYNCHRONOUSLY -- a native `InvalidStateError` if the browser considers a
        // session already starting/active, which can happen even right after the `.abort()` above if
        // the previous session's teardown hasn't fully landed yet. Confirmed live as a real bug
        // otherwise: an uncaught throw here was propagating up into whatever called `start()`
        // (continuous "conversation mode" in VoiceOrbExperience.tsx calls it from inside an async
        // function), and `setListening(true)` below never ran -- the UI was left claiming nothing while
        // genuinely not listening, with no visible error, permanently breaking the conversation loop
        // after the first exchange. Caught here and surfaced as a real, visible error instead of left to
        // silently break whatever called this.
        try {
            recognition.start();
            setListening(true);
        }
        catch {
            // Likely transient (the native session hasn't finished releasing yet) -- one retry after a
            // short delay before giving up for real, rather than immediately breaking the conversation
            // loop over what's often just bad timing.
            setTimeout(() => {
                if (recognitionRef.current !== recognition)
                    return; // superseded by a newer start() call
                try {
                    recognition.start();
                    setListening(true);
                }
                catch {
                    recognitionRef.current = null;
                    setError("Couldn't start listening -- try tapping the mic again.");
                    setListening(false);
                }
            }, 400);
        }
    }, [onResult]);
    return { start, stop, listening, error, supported };
}
